import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, fmtDateTime, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Clock, Plus, Filter } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PhotoAttach from "../components/PhotoAttach";

const CATEGORIES = [
    { key: "HOUSEKEEPING", label: "Housekeeping" },
    { key: "EQUIPMENT", label: "Equipment" },
    { key: "PANTRY", label: "Pantry" },
    { key: "SECURITY", label: "Security" },
    { key: "CIVIL_ELECTRICAL", label: "Civil / Electrical" },
    { key: "IT_AV", label: "IT / AV" },
    { key: "OTHER", label: "Other" },
];
const SLA_CHECKS = [
    { key: "INCL", label: "INCL — included in fee" },
    { key: "BILLED", label: "BILLED — chargeable extra" },
    { key: "CLIENT", label: "CLIENT — client scope" },
    { key: "NOT_IN_AGREEMENT", label: "NOT IN AGREEMENT — refer to HO" },
];
const STATUS_COLORS = {
    OPEN: "bg-amber-50 text-amber-700 border-amber-200",
    PM_INFORMED: "bg-indigo-50 text-indigo-700 border-indigo-200",
    HO_CLASSIFIED: "bg-blue-50 text-blue-700 border-blue-200",
    QUOTATION_SENT: "bg-purple-50 text-purple-700 border-purple-200",
    CLIENT_APPROVED: "bg-teal-50 text-teal-700 border-teal-200",
    WORK_IN_PROGRESS: "bg-cyan-50 text-cyan-700 border-cyan-200",
    CLOSED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_LABEL = {
    OPEN: "Open", PM_INFORMED: "PM informed", HO_CLASSIFIED: "HO classified",
    QUOTATION_SENT: "Quotation sent", CLIENT_APPROVED: "Client approved",
    WORK_IN_PROGRESS: "Work in progress", CLOSED: "Closed",
};

export default function ServiceRequestsPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [aging, setAging] = useState({});
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [bucketFilter, setBucketFilter] = useState("");
    const [, tick] = useState(0);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (bucketFilter) params.bucket = bucketFilter;
            const [srs, ag] = await Promise.all([
                api.get("/service-requests", { params }),
                api.get("/service-requests/aging-summary"),
            ]);
            setRows(srs.data); setAging(ag.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [statusFilter, bucketFilter]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(id); }, []);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Service Requests</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Incidents & Requests</h1>
                    <p className="text-sm text-gray-500 mt-1">Full workflow. Escalations auto-open here at status "Open".</p>
                </div>
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="sr-new-btn"><Plus className="w-4 h-4" /> Raise new</button>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <BucketCard label="Open (total)" value={aging.total_open || 0} onClick={() => setBucketFilter("")} active={!bucketFilter} testid="bucket-total" />
                <BucketCard label="< 24 h" value={aging["<24h"] || 0} onClick={() => setBucketFilter("<24h")} active={bucketFilter === "<24h"} testid="bucket-lt24" />
                <BucketCard label="24 – 48 h" value={aging["24-48h"] || 0} onClick={() => setBucketFilter("24-48h")} active={bucketFilter === "24-48h"} accent="amber" testid="bucket-24-48" />
                <BucketCard label="> 48 h" value={aging[">48h"] || 0} onClick={() => setBucketFilter(">48h")} active={bucketFilter === ">48h"} accent="red" testid="bucket-gt48" />
            </div>
            {aging.breached > 0 && (
                <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-[#E02B2B] inline-flex items-center gap-2" data-testid="breach-banner">
                    <AlertTriangle className="w-4 h-4" /> {aging.breached} request{aging.breached > 1 ? "s" : ""} breached SLA — PM notified.
                </div>
            )}

            <div className="mt-4 flex items-center gap-2">
                <div className="text-xs text-gray-500 inline-flex items-center gap-1"><Filter className="w-3 h-3" /> Filter status:</div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs rounded border border-gray-200 px-2 py-1 bg-white" data-testid="sr-status-filter">
                    <option value="">All</option>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                {loading ? <div className="p-6 text-center text-sm text-gray-500">Loading…</div> :
                rows.length === 0 ? <div className="p-8 text-center text-sm text-gray-500" data-testid="sr-empty">No matching requests.</div> :
                <ul className="divide-y divide-gray-100" data-testid="sr-list">
                    {rows.map((r) => <SRRow key={r.id} r={r} />)}
                </ul>}
            </div>

            {creating && <CreateSRDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
        </div>
    );
}

function BucketCard({ label, value, onClick, active, accent, testid }) {
    const cls = accent === "red" ? "border-red-200 text-[#E02B2B]" : accent === "amber" ? "border-amber-200 text-amber-700" : "border-gray-100 text-[#1E3355]";
    return (
        <button onClick={onClick} className={`rounded-xl bg-white border p-4 text-left transition-colors ${active ? "ring-2 ring-[#1E3355]" : ""} ${cls} fx-shadow-card hover:border-[#1E3355]`} data-testid={testid}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`font-display text-2xl font-bold mt-1 ${accent === "red" ? "text-[#E02B2B]" : accent === "amber" ? "text-amber-700" : "text-[#1E3355]"}`}>{value}</div>
        </button>
    );
}

function SRRow({ r }) {
    const ageH = Math.floor(r.age_minutes / 60);
    const ageM = r.age_minutes % 60;
    return (
        <Link to={`/service-requests/${r.id}`} className={`block p-4 sm:p-5 hover:bg-[#FAFBFC] ${r.is_breached ? "bg-red-50/60" : ""}`} data-testid={`sr-row-${r.id}`}>
            <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${r.is_breached ? "bg-red-100 text-[#E02B2B]" : "bg-gray-100 text-gray-500"}`}>
                    <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display font-semibold text-[#1E3355]">{r.site_name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-gray-500">{r.category}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        <SLACheckBadge check={r.sla_check} />
                        {r.asset_item_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`sr-asset-${r.id}`}>Asset: {r.asset_item_name}</span>
                        )}
                        {r.is_breached && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-[#E02B2B] border border-red-200" data-testid={`sr-breach-${r.id}`}>
                                <AlertTriangle className="w-3 h-3" /> BREACHED
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-sm text-gray-800 line-clamp-2">{r.description}</div>
                    <div className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Raised {ageH}h {ageM}m ago · by {r.created_by_name}</div>
                    {r.breach_reasons?.length > 0 && (
                        <div className="mt-1 text-[11px] text-[#E02B2B]">{r.breach_reasons.join(" · ")}</div>
                    )}
                </div>
            </div>
        </Link>
    );
}

function SLACheckBadge({ check }) {
    const map = { INCL: "bg-emerald-50 text-emerald-700 border-emerald-200", BILLED: "bg-amber-50 text-amber-700 border-amber-200", CLIENT: "bg-gray-50 text-gray-500 border-gray-200", NOT_IN_AGREEMENT: "bg-red-50 text-[#E02B2B] border-red-200" };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[check] || ""}`}>{check}</span>;
}

function CreateSRDialog({ onClose, onCreated }) {
    const [sites, setSites] = useState([]);
    const [form, setForm] = useState({ site_id: "", category: "HOUSEKEEPING", sla_check: "INCL", description: "", date: new Date().toISOString().slice(0, 10) });
    const [created, setCreated] = useState(null); // {id, site_id}
    useEffect(() => { api.get("/sites").then(({ data }) => {
        // Service requests only apply to MANAGED_CLIENT sites — filter creation picker.
        const managed = data.filter((s) => (s.site_type || "MANAGED_CLIENT") === "MANAGED_CLIENT");
        setSites(managed);
        setForm((f) => ({ ...f, site_id: f.site_id || managed[0]?.id }));
    }); }, []);
    async function submit(e) {
        e.preventDefault();
        try {
            const { data } = await api.post("/service-requests", form);
            toast.success("Service request raised");
            setCreated(data);
        } catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="sr-create-modal">
            <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Raise service request</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-0.5">New request</div>
                </div>
                {!created ? (
                    <form onSubmit={submit} className="p-5 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Site">
                                <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} required className={fieldCls} data-testid="sr-site">
                                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </Field>
                            <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={fieldCls} data-testid="sr-date" /></Field>
                        </div>
                        <Field label="Category">
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required className={fieldCls} data-testid="sr-category">
                                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </Field>
                        <Field label="SLA check">
                            <select value={form.sla_check} onChange={(e) => setForm({ ...form, sla_check: e.target.value })} required className={fieldCls} data-testid="sr-sla-check">
                                {SLA_CHECKS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                            <div className="mt-1 text-[10px] text-gray-500">
                                Tip: check <Link to={form.site_id ? `/sla/${form.site_id}` : "#"} className="underline text-[#1E3355]">the SLA lookup</Link> before choosing.
                            </div>
                        </Field>
                        <Field label="Description">
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} required className={fieldCls} data-testid="sr-description" />
                        </Field>
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="sr-create-cancel">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold" data-testid="sr-create-submit">Raise & attach photos</button>
                        </div>
                    </form>
                ) : (
                    <div className="p-5 space-y-3">
                        <div className="text-sm text-gray-700">Now attach any <span className="font-semibold">before photos</span> (recommended for escalations & incidents).</div>
                        <PhotoAttach parentType="service_request" parentId={created.id} siteId={created.site_id} tag="before" testIdPrefix="sr-create-before-photo" />
                        <button onClick={onCreated} className="w-full py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold" data-testid="sr-create-done">Done</button>
                    </div>
                )}
            </div>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) { return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>); }
